import {
  Chip,
  Css,
  FilterDefs,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  PageHeaderLayout,
  RowStyles,
  SimpleHeaderAndData,
  Tag,
  Tooltip,
  actionColumn,
  column,
  multiFilter,
  simpleDataRows,
  simpleHeader,
  toggleFilter,
  useGridTableLayoutState,
} from "@homebound/beam";
import { useCallback, useMemo } from "react";
import { formatDate } from "src/components";
import { DesignPackageFilter, DesignPackageLocation, ReadyPlanStatus } from "src/generated/graphqlTypes";
import {
  DesignCatalogsTableQuery,
  DesignPackageTableItemFragment,
  useDesignCatalogsTableQuery,
} from "src/routes/libraries/design-catalog/DesignCatalogsPage.generated";
import { createDesignPackageUrl } from "src/RouteUrls";
import { isDefined } from "src/utils";
import { DesignPackageButtonMenu } from "./design-package-configurator/components/DesignPackageButtonMenu";
import { designUpgradeTagColor, planPackageTagColor } from "./designCatalogAtoms";
import { useNewDesignCatalogWizard } from "./new-design-catalog-wizard/useNewDesignCatalogWizard";

type DesignPackagesPageFilter = { location?: DesignPackageLocation[]; archived?: boolean };

export function DesignCatalogsPage() {
  const openNewDesignCatalogWizard = useNewDesignCatalogWizard();
  const filterDefs: FilterDefs<DesignPackagesPageFilter> = useMemo(
    () => ({
      location: multiFilter({
        label: "Type",
        options: [
          { value: DesignPackageLocation.Interior, label: "Interior" },
          { value: DesignPackageLocation.Exterior, label: "Exterior" },
        ],
        getOptionValue: (o) => o.value,
        getOptionLabel: (o) => o.label,
      }),
      archived: toggleFilter({ label: "Show Archived" }),
    }),
    [],
  );
  const layoutState = useGridTableLayoutState<DesignPackagesPageFilter>({
    persistedFilter: { filterDefs, storageKey: "design-package-filter" },
    search: "client",
  });
  const query = useDesignCatalogsTableQuery({ variables: { filter: mapToFilter(layoutState.filter) } });
  const createRows = useCallback(
    (data: DesignCatalogsTableQuery | undefined) => {
      const packages = data?.designPackages.entities ?? [];
      const locations = layoutState.filter.location;
      const filtered = !locations?.nonEmpty ? packages : packages.filter((dp) => locations.includes(dp.location.code));
      return createRowsFromPackages(filtered);
    },
    [layoutState.filter.location],
  );

  return (
    <PageHeaderLayout
      pageHeader={{ title: "Design Packages", actions: [{ label: "Create", onClick: openNewDesignCatalogWizard }] }}
    >
      <GridTableLayout
        layoutState={layoutState}
        hideEditColumns
        tableProps={{
          query,
          createRows,
          columns,
          rowStyles,
          sorting: { on: "client", initial: ["name", "ASC"] },
          emptyFallback: "No design packages found.",
        }}
      />
    </PageHeaderLayout>
  );
}

export const defaultDesignCatalogsFilter: DesignPackageFilter = { status: [ReadyPlanStatus.Active] };

function mapToFilter(filter: DesignPackagesPageFilter): DesignPackageFilter {
  return filter.archived ? { status: [ReadyPlanStatus.Active, ReadyPlanStatus.Archived] } : defaultDesignCatalogsFilter;
}

type Row = SimpleHeaderAndData<DesignPackageTableItemFragment>;

const rowStyles: RowStyles<Row> = {
  data: { rowLink: ({ id, data }) => createDesignPackageUrl(id, data.aggregateLatest.id) },
};

function createRowsFromPackages(items: DesignPackageTableItemFragment[]): GridDataRow<Row>[] {
  return [simpleHeader, ...simpleDataRows(items)];
}

const columns: GridColumn<Row>[] = [
  column<Row>({
    id: "name",
    header: "Name",
    data: (designPackage) => ({
      content: () => (
        <div css={Css.df.gap1.aic.$}>
          {designPackage.name}
          <div>{designPackage.code}</div>
          {designPackage.status.code === ReadyPlanStatus.Archived && <Tag text="Archived" type="warning" />}
          {designPackage.availableUpdates.nonEmpty && (
            <Tooltip
              title={designPackage.availableUpdates
                .map((u) => `${u.planVersion.readyPlan.name} - v${u.planVersion.version}`)
                .join(", ")}
            >
              <Tag iconOnly icon="arrowFromBottom" type="update" text="Updates available" />
            </Tooltip>
          )}
        </div>
      ),
      value: designPackage.name,
    }),
    mw: "240px",
  }),
  column<Row>({
    id: "location",
    header: "Type",
    data: (designPackage) => ({
      content: () => <Tag variant="secondary" text={designPackage.location.name} />,
      value: designPackage.location.name,
    }),
    mw: "100px",
  }),
  column<Row>({
    id: "options",
    header: "Options",
    data: (designPackage) => {
      const baseRpos = designPackage.options.filter((rpo) => rpo.globalOption.group.forDesignPackages);
      const upgradeRpos = designPackage.options.filter((rpo) => rpo.globalOption.group.forDesignUpgrade);
      const planRpos = designPackage.options.filter((rpo) => rpo.globalOption.isPlanSpecific);

      return (
        <div css={Css.df.fdr.aic.gapPx(4).$}>
          {Object.entries(baseRpos.groupBy((rpo) => rpo.globalOption.group.id)).map(([gogId, rpos]) => {
            const bgColor = rpos.first!.globalOption.group.designPackageTagColor;
            return (
              <Chip
                key={gogId}
                title={`${rpos.length} ${rpos.first!.globalOption.group.name} options`}
                text={String(rpos.length)}
                xss={Css.if(isDefined(bgColor)).bgColor(bgColor!).white.$}
              />
            );
          })}
          {upgradeRpos.nonEmpty && (
            <Chip
              text={upgradeRpos.length.toString()}
              title={`${upgradeRpos.length} upgrades`}
              xss={Css.bgColor(designUpgradeTagColor).white.$}
            />
          )}
          {planRpos.nonEmpty && (
            <Chip
              text={planRpos.length.toString()}
              title={`${planRpos.length} plan options`}
              xss={Css.bgColor(planPackageTagColor).white.$}
            />
          )}
        </div>
      );
    },
    clientSideSort: false,
    w: 1,
    mw: "160px",
  }),
  column<Row>({
    id: "updated",
    header: "Last Updated",
    data: (designPackage) => ({
      content: () => formatDate(designPackage.updatedAt),
      sortValue: designPackage.updatedAt,
    }),
    align: "right",
    mw: "160px",
  }),
  actionColumn<Row>({
    id: "actions",
    header: "",
    data: (designPackage) => <DesignPackageButtonMenu designPackage={designPackage} />,
    clientSideSort: false,
    align: "right",
    w: "60px",
  }),
];
